import React, { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Badge from '@mui/material/Badge';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Paper from '@mui/material/Paper';
import Switch from '@mui/material/Switch';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import ArrowBack from '@mui/icons-material/ArrowBack';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import FilterList from '@mui/icons-material/FilterList';
import PageHeader from '../../../Components/PageHeader';
import TablePeo from '../../../Components/TablePeo';
import { useApiGet } from '../../../utils/httpClientTs';
import {
  downloadPickListsPdf,
  generateVendorBatches,
  getOrderStatuses,
  listManagementOrders,
} from '../../../endpoints/chapterSupplies';
import type { ManagementOrderSummary } from '../../../types/chapterSupplies';

const formatPrice = (v: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
    v || 0
  );

const formatBatchType = (batchType?: string) => {
  switch (batchType) {
    case 'chapter_pins':
      return 'Chapter pins';
    case 'state_pins':
      return 'State pins';
    default:
      return batchType || '—';
  }
};

type PaymentMethodFilter = '' | 'card' | 'check';

type Row = ManagementOrderSummary & {
  id: string;
  order_display: string;
  fulfillment_display: string;
  memo_display: string;
};

const headCells = [
  {
    id: 'order_display',
    firstCell: true,
    label: 'Order',
    width: '15%',
    sortable: false,
  },
  {
    id: 'placed_at',
    label: 'Placed',
    width: '18%',
    sortable: false,
    format: (v: unknown) => (v ? new Date(String(v)).toLocaleString() : ''),
  },
  { id: 'order_status_name', label: 'Status', width: '12%', sortable: false },
  { id: 'memo_display', label: 'Memo', width: '12%', sortable: false },
  {
    id: 'fulfillment_display',
    label: 'Fulfillment',
    width: '14%',
    sortable: false,
  },
  {
    id: 'purchaser_display_name',
    label: 'Purchaser',
    width: '18%',
    sortable: false,
  },
  { id: 'ship_to_name', label: 'Ship to', width: '15%', sortable: false },
  {
    id: 'total',
    label: 'Total',
    width: '12%',
    sortable: false,
    format: (v: unknown) => formatPrice(Number(v)),
  },
];

const memoFilterLabel = (value: string) => {
  switch (value) {
    case 'open_any':
      return 'Open memo (any)';
    case 'open_credit':
      return 'Open credit';
    case 'open_debit':
      return 'Open debit';
    default:
      return '';
  }
};

export default function ManagementOrdersList() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [orderBy, setOrderBy] = useState('placed_at');
  const [statusFilter, setStatusFilter] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [checkNumberInput, setCheckNumberInput] = useState('');
  const [debouncedCheckNumber, setDebouncedCheckNumber] = useState('');
  const [paymentMethodFilter, setPaymentMethodFilter] =
    useState<PaymentMethodFilter>('');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState<(string | number)[]>([]);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [batchError, setBatchError] = useState<string | null>(null);
  const [batchSuccess, setBatchSuccess] = useState<string | null>(null);
  const [batchLoading, setBatchLoading] = useState(false);
  const [internalFulfillmentOpenOnly, setInternalFulfillmentOpenOnly] =
    useState(false);
  const [memoFilter, setMemoFilter] = useState('');

  useEffect(() => {
    const t = window.setTimeout(
      () => setDebouncedSearch(searchInput.trim()),
      400
    );
    return () => window.clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    const t = window.setTimeout(
      () => setDebouncedCheckNumber(checkNumberInput.trim()),
      400
    );
    return () => window.clearTimeout(t);
  }, [checkNumberInput]);

  const purchaserQ = useMemo(() => {
    if (debouncedSearch.length === 0) return undefined;
    if (debouncedSearch.length >= 2) return debouncedSearch;
    return undefined;
  }, [debouncedSearch]);

  const checkNumberQ = useMemo(() => {
    if (debouncedCheckNumber.length === 0) return undefined;
    return debouncedCheckNumber;
  }, [debouncedCheckNumber]);

  const expandedFilterCount = useMemo(() => {
    let count = 0;
    if (paymentMethodFilter) count += 1;
    if (checkNumberQ) count += 1;
    if (memoFilter) count += 1;
    return count;
  }, [paymentMethodFilter, checkNumberQ, memoFilter]);

  const clearExpandedFilters = () => {
    setPaymentMethodFilter('');
    setCheckNumberInput('');
    setDebouncedCheckNumber('');
    setMemoFilter('');
  };

  useEffect(() => {
    setPage(0);
    setSelectedRows([]);
  }, [
    statusFilter,
    purchaserQ,
    checkNumberQ,
    paymentMethodFilter,
    internalFulfillmentOpenOnly,
    memoFilter,
  ]);

  const memoListParams = useMemo(() => {
    switch (memoFilter) {
      case 'open_credit':
        return { has_open_memo: true, memo_type: 'credit' as const };
      case 'open_debit':
        return { has_open_memo: true, memo_type: 'debit' as const };
      case 'open_any':
        return { has_open_memo: true };
      default:
        return {};
    }
  }, [memoFilter]);

  const limit = rowsPerPage;
  const offset = page * rowsPerPage;

  const { data, isFetching, error } = useApiGet(
    [
      'chapter-supplies-management-orders',
      limit,
      offset,
      statusFilter,
      purchaserQ ?? '',
      checkNumberQ ?? '',
      paymentMethodFilter,
      internalFulfillmentOpenOnly,
      memoFilter,
    ],
    () =>
      listManagementOrders({
        limit,
        offset,
        order_status_id: statusFilter || undefined,
        q: purchaserQ,
        check_number: checkNumberQ,
        payment_method: paymentMethodFilter || undefined,
        internal_fulfillment_open: internalFulfillmentOpenOnly || undefined,
        ...memoListParams,
      }),
    { enabled: true, retry: 1 }
  );

  const { data: orderStatuses = [] } = useApiGet(
    ['chapter-supplies-order-statuses'],
    getOrderStatuses,
    { retry: 1 }
  );

  const tableRows: Row[] = useMemo(
    () =>
      (data?.data ?? []).map((o) => ({
        ...o,
        id: o.order_id,
        order_display:
          o.order_number != null ? `#${o.order_number}` : o.order_id,
        memo_display: (() => {
          if (!o.payment_memo_type) return '—';
          const type = o.payment_memo_type === 'credit' ? 'Credit' : 'Debit';
          const status = o.payment_memo_status || '';
          const bal = o.payment_memo_balance_remaining;
          if (status === 'open' && bal != null && bal > 0) {
            return `${type} open (${formatPrice(bal)})`;
          }
          return `${type} ${status || ''}`.trim();
        })(),
        fulfillment_display: (() => {
          const base = o.is_vendor_direct
            ? o.vendor_batches && o.vendor_batches.length > 0
              ? `${
                  o.vendor_batches.every(
                    (batch) => batch.batch_status === 'sent'
                  )
                    ? 'Vendor sent'
                    : 'Vendor batched'
                }: ${o.vendor_batches
                  .map((batch) => formatBatchType(batch.batch_type))
                  .join(', ')}`
              : 'Vendor direct'
            : 'Internal';
          if (o.is_vendor_direct && o.internal_fulfillment_open) {
            return `${base} · Org ship pending`;
          }
          return base;
        })(),
      })),
    [data?.data]
  );

  const handleRowClick = (_event: React.MouseEvent, rowId: string | number) => {
    navigate(`/chapter-supplies-management/orders/${rowId}`);
  };

  const handlePickLists = async () => {
    const ids = selectedRows.map(String).filter(Boolean);
    if (ids.length === 0) return;
    const selectedVendorOnly = tableRows.filter(
      (row) => ids.includes(row.order_id) && !row.has_internal_fulfillment_lines
    );
    if (selectedVendorOnly.length > 0) {
      setPdfError(
        'Orders with only vendor-direct fulfillment cannot be included in pick lists. Mixed orders will include org-shipped lines only.'
      );
      return;
    }
    setPdfError(null);
    setPdfLoading(true);
    try {
      const blob = await downloadPickListsPdf(ids);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `pick-lists-${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      setPdfError(
        e instanceof Error ? e.message : 'Could not generate pick lists PDF.'
      );
    } finally {
      setPdfLoading(false);
    }
  };

  const handleGenerateVendorBatches = async () => {
    setBatchError(null);
    setBatchSuccess(null);
    setBatchLoading(true);
    try {
      const resp = await generateVendorBatches();
      setBatchSuccess(
        resp.orders_processed > 0
          ? `Generated ${resp.batches.length} vendor batch${resp.batches.length === 1 ? '' : 'es'} for ${resp.orders_processed} order${resp.orders_processed === 1 ? '' : 's'}.`
          : 'No eligible vendor-direct orders were waiting to be batched.'
      );
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['chapter-supplies-management-orders'],
        }),
        queryClient.invalidateQueries({
          queryKey: ['chapter-supplies-vendor-batches'],
        }),
      ]);
    } catch (e) {
      setBatchError(
        e instanceof Error ? e.message : 'Could not generate vendor batches.'
      );
    } finally {
      setBatchLoading(false);
    }
  };

  const searchHint =
    debouncedSearch.length === 1
      ? 'Enter at least 2 characters to search by purchaser, ship-to, or order number.'
      : null;

  const checkNumberDisabled = paymentMethodFilter === 'card';

  return (
    <Grid container paddingLeft={2} paddingRight={2}>
      <Grid item xs={12}>
        <PageHeader
          title="Customer orders"
          leftDom={
            <Button
              startIcon={<ArrowBack />}
              onClick={() => navigate('/chapter-supplies-management')}
            >
              Back
            </Button>
          }
          rightDom={undefined}
          subtitle="Storefront orders — search by purchaser, ship-to, or order number; use Filters for payment type, check number, and memos."
        />
      </Grid>
      <Grid item xs={12}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Unable to load orders.
          </Alert>
        )}
        {pdfError && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            onClose={() => setPdfError(null)}
          >
            {pdfError}
          </Alert>
        )}
        {batchError && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            onClose={() => setBatchError(null)}
          >
            {batchError}
          </Alert>
        )}
        {batchSuccess && (
          <Alert
            severity="success"
            sx={{ mb: 2 }}
            onClose={() => setBatchSuccess(null)}
          >
            {batchSuccess}
          </Alert>
        )}

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          justifyContent="flex-end"
          sx={{ mb: 2 }}
        >
          <Button
            variant="outlined"
            onClick={() =>
              navigate('/chapter-supplies-management/vendor-batches')
            }
          >
            Vendor batches
          </Button>
          <Button
            variant="outlined"
            disabled={batchLoading}
            onClick={() => void handleGenerateVendorBatches()}
          >
            {batchLoading ? 'Generating…' : 'Generate vendor batches'}
          </Button>
          <Button
            variant="contained"
            disabled={selectedRows.length === 0 || pdfLoading}
            onClick={() => void handlePickLists()}
          >
            {pdfLoading ? 'Generating…' : 'Create pick lists'}
          </Button>
        </Stack>

        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems={{ md: 'center' }}
          >
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel id="cs-order-status-filter">Status</InputLabel>
              <Select
                labelId="cs-order-status-filter"
                label="Status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(String(e.target.value))}
              >
                <MenuItem value="">All statuses</MenuItem>
                {orderStatuses.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              label="Search orders"
              placeholder="Purchaser, ship-to, or order # (min 2 chars)"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              sx={{ minWidth: 260, flexGrow: 1 }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={internalFulfillmentOpenOnly}
                  onChange={(_, checked) =>
                    setInternalFulfillmentOpenOnly(checked)
                  }
                  color="primary"
                />
              }
              label="Org fulfillment open"
            />
            <Badge
              badgeContent={expandedFilterCount}
              color="primary"
              invisible={expandedFilterCount === 0}
            >
              <Button
                variant="outlined"
                onClick={() => setFiltersOpen((open) => !open)}
                startIcon={<FilterList />}
                endIcon={filtersOpen ? <ExpandLess /> : <ExpandMore />}
              >
                Filters
              </Button>
            </Badge>
          </Stack>

          <Collapse in={filtersOpen}>
            <Box
              sx={{
                mt: 2,
                pt: 2,
                borderTop: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                alignItems={{ md: 'flex-start' }}
              >
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel id="cs-payment-method-filter">
                    Payment type
                  </InputLabel>
                  <Select
                    labelId="cs-payment-method-filter"
                    label="Payment type"
                    value={paymentMethodFilter}
                    onChange={(e) => {
                      const value = String(
                        e.target.value
                      ) as PaymentMethodFilter;
                      setPaymentMethodFilter(value);
                      if (value === 'card') {
                        setCheckNumberInput('');
                        setDebouncedCheckNumber('');
                      }
                    }}
                  >
                    <MenuItem value="">All payments</MenuItem>
                    <MenuItem value="card">Card</MenuItem>
                    <MenuItem value="check">Check</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  size="small"
                  label="Check number"
                  placeholder="Match check # on order"
                  value={checkNumberInput}
                  onChange={(e) => setCheckNumberInput(e.target.value)}
                  disabled={checkNumberDisabled}
                  sx={{ minWidth: 200 }}
                />
                <FormControl size="small" sx={{ minWidth: 220 }}>
                  <InputLabel id="cs-memo-filter">Payment memo</InputLabel>
                  <Select
                    labelId="cs-memo-filter"
                    label="Payment memo"
                    value={memoFilter}
                    onChange={(e) => setMemoFilter(String(e.target.value))}
                  >
                    <MenuItem value="">All orders</MenuItem>
                    <MenuItem value="open_any">Open memo (any)</MenuItem>
                    <MenuItem value="open_credit">Open credit</MenuItem>
                    <MenuItem value="open_debit">Open debit</MenuItem>
                  </Select>
                </FormControl>
                <Button
                  variant="text"
                  onClick={clearExpandedFilters}
                  disabled={expandedFilterCount === 0}
                  sx={{ alignSelf: { md: 'center' } }}
                >
                  Clear filters
                </Button>
              </Stack>
            </Box>
          </Collapse>

          {!filtersOpen && expandedFilterCount > 0 && (
            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1.5 }}>
              {paymentMethodFilter && (
                <Chip
                  size="small"
                  label={paymentMethodFilter === 'card' ? 'Card' : 'Check'}
                  onDelete={() => setPaymentMethodFilter('')}
                />
              )}
              {checkNumberQ && (
                <Chip
                  size="small"
                  label={`Check #${checkNumberQ}`}
                  onDelete={() => {
                    setCheckNumberInput('');
                    setDebouncedCheckNumber('');
                  }}
                />
              )}
              {memoFilter && (
                <Chip
                  size="small"
                  label={memoFilterLabel(memoFilter)}
                  onDelete={() => setMemoFilter('')}
                />
              )}
            </Stack>
          )}
        </Paper>

        {searchHint && (
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            sx={{ mb: 1 }}
          >
            {searchHint}
          </Typography>
        )}

        <TablePeo
          data={tableRows}
          isFetching={isFetching}
          headCells={headCells}
          handleRowClick={handleRowClick}
          page={page}
          setPage={setPage}
          rowsPerPage={rowsPerPage}
          setRowsPerPage={setRowsPerPage}
          order={order}
          setOrder={setOrder}
          orderBy={orderBy}
          setOrderBy={setOrderBy}
          emptyMessage="No storefront orders match your filters."
          selectable
          selectedRows={selectedRows}
          onSelectionChange={setSelectedRows}
          remoteTotalCount={data?.total}
        />
      </Grid>
    </Grid>
  );
}
